import {PaymentMethod} from '@stripe/stripe-js'
import {TeamMember} from 'auth/user'
import {useTrackReferral} from 'lib/first-promoter'
import {api} from 'lib/url'
import {useObvioAuth, useObvioUser} from 'obvio/auth'
import {Plan, PlanName, PlanInfo, BillingInterval} from 'obvio/Billing/plans'
import {teamMemberClient} from 'obvio/obvio-client'

export const SUBSCRIPTION_TYPE_ACTIVE = 'active'
export const SUBSCRIPTION_TYPE_TRIALING = 'trialing'
export const SUBSCRIPTION_TYPE_CANCELLED = 'canceled'
export const SUBSCRIPTION_TYPE_PAST_DUE = 'past_due'
export const SUBSCRIPTION_TYPE_UNPAID = 'unpaid'

const DELINQUENT_STATUSES = [
  SUBSCRIPTION_TYPE_PAST_DUE,
  SUBSCRIPTION_TYPE_UNPAID,
] as const

export interface Subscription {
  id: number
  plan: Plan
  renew_plan: PlanName | null
  renews_at: string | null
  stripe_status: string
  ends_at: string | null
  trial_ends_at: string | null
}

export function useSubscribe() {
  const url = api(`/subscribe`)
  const {setUser} = useObvioAuth()
  const trackReferral = useTrackReferral()

  return (
    plan: PlanInfo,
    paymentMethod: PaymentMethod,
    interval: BillingInterval,
  ) => {
    return teamMemberClient
      .post<TeamMember>(url, {
        plan: plan.name,
        payment_method_id: paymentMethod.id,
        interval: interval,
      })
      .then((user) => {
        setUser(user)
        trackReferral(user)
      })
  }
}

export function useCancel() {
  const url = api(`/subscribe`)
  const {setUser} = useObvioAuth()

  return () => {
    return teamMemberClient.delete<TeamMember>(url).then(setUser)
  }
}

export function useResume() {
  const url = api(`/subscribe`)
  const {setUser} = useObvioAuth()

  return (plan: PlanInfo, interval?: BillingInterval) => {
    return teamMemberClient
      .put<TeamMember>(url, {
        plan: plan.name,
        interval: interval,
      })
      .then(setUser)
  }
}

export const useGetSubscription = (planName?: string) => {
  const user = useObvioUser()

  if (!user.subscriptions) {
    return
  }

  if (planName) {
    return user.subscriptions.find(
      (s) => s.plan?.name === planName && isActive(s),
    )
  }

  return user.subscriptions.find(isActive)
}

export const isActive = (subscription?: Subscription) => {
  if (!subscription) {
    return false
  }

  return (
    subscription.stripe_status === SUBSCRIPTION_TYPE_ACTIVE ||
    subscription.stripe_status === SUBSCRIPTION_TYPE_TRIALING
  )
}

export const isDelinquent = (subscription?: Subscription) => {
  if (!subscription) {
    return false
  }

  return DELINQUENT_STATUSES.includes(
    subscription.stripe_status as typeof DELINQUENT_STATUSES[number],
  )
}

export const useGetDelinquentSubscription = () => {
  const user = useObvioUser()

  if (!user.subscriptions) {
    return
  }

  return user.subscriptions.find(isDelinquent)
}

export function useRetrySubscriptionPayment() {
  const url = api('/subscribe/retry-payment')
  const {setUser} = useObvioAuth()

  return () => teamMemberClient.post<TeamMember>(url).then(setUser)
}

export function useRecoverSubscriptionPayment() {
  const url = api('/subscribe/recover-payment')
  const {setUser} = useObvioAuth()

  return (paymentMethodId: string) =>
    teamMemberClient
      .post<TeamMember>(url, {payment_method_id: paymentMethodId})
      .then(setUser)
}
